<main class="main" *ngIf="moduleConfig">

  <!-- Breadcrumb -->
  <div class="breadcrumb">
    <span>Home</span>
    <span class="bsep">›</span>
    <span>Schema Onboarding</span>
    <span class="bsep">›</span>
    <span class="cur">{{ moduleConfig.label }}</span>
  </div>

  <!-- Page header -->
  <div class="page-header">
    <div class="page-title">
      {{ moduleConfig.label }}
      <span class="badge" *ngIf="activeModule !== 'portfolio' || hierarchyMode === 'import'">
        Step {{ state.step }} of 4
      </span>
    </div>
    <div class="page-subtitle">{{ moduleConfig.subtitle }}</div>
  </div>

  <!-- Hierarchy mode toggle — portfolio only -->
  <div class="mode-toggle" *ngIf="activeModule === 'portfolio'">
    <button
      class="mode-btn"
      [class.active]="hierarchyMode === 'browse'"
      (click)="hierarchyMode = 'browse'"
    >
      <span class="material-symbols-outlined">account_tree</span>
      Hierarchy Import
    </button>
    <button
      class="mode-btn"
      [class.active]="hierarchyMode === 'import'"
      (click)="hierarchyMode = 'import'"
    >
      <span class="material-symbols-outlined">upload_file</span>
      New Import
    </button>
  </div>

  <!-- Hierarchy browser -->
  <app-hierarchy-browser
    *ngIf="activeModule === 'portfolio' && hierarchyMode === 'browse'"
  ></app-hierarchy-browser>

  <!-- Import wizard — hidden in browse mode -->
  <ng-container *ngIf="activeModule !== 'portfolio' || hierarchyMode === 'import'">

  <!-- Step bar -->
  <app-step-bar [steps]="moduleConfig.steps" [currentStep]="state.step"></app-step-bar>

  <!-- Import Brief — shown in steps 1–3 -->
  <div class="card" *ngIf="state.step < 4">
    <div class="card-title" style="margin-bottom: 12px;">
      <span class="material-symbols-outlined">info</span>
      About this Import
    </div>
    <app-import-brief [brief]="moduleConfig.brief" [requiredLabels]="requiredFieldLabels"></app-import-brief>
  </div>

  <!-- ─── STEP 1: UPLOAD ─── -->
  <ng-container *ngIf="state.step === 1">
    <div class="card">
      <div class="card-header">
        <div class="card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Upload your data
        </div>
        <span class="tag tag-navy">Step 1 of 4</span>
      </div>

      <div class="hint-label">How this data maps in Kepler</div>
      <app-schema-display [schema]="moduleConfig.schema"></app-schema-display>
      <app-upload-zone (fileSelected)="onFileSelected($event)"></app-upload-zone>

      <!-- File loaded state -->
      <ng-container *ngIf="state.cols.length > 0">
        <div class="file-loaded">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="20" height="20">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          <div class="file-info">
            <div class="file-name">{{ state.fileName }}</div>
            <div class="file-meta">{{ state.cols.length }} columns · {{ state.rows.length }} rows</div>
          </div>
          <button class="btn-remove" (click)="onRemoveFile()">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="preview-header">
          <span class="hint-label">Preview (first 5 rows)</span>
          <span class="tag tag-teal">{{ state.cols.length }} columns detected</span>
        </div>
        <app-preview-table [cols]="state.cols" [rows]="state.rows"></app-preview-table>
      </ng-container>
    </div>

    <div class="actions">
      <button class="btn btn-teal" [disabled]="!state.cols.length" (click)="goStep(2)">
        Continue
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>
    </div>
  </ng-container>

  <!-- ─── STEP 2: MAP FIELDS ─── -->
  <ng-container *ngIf="state.step === 2">

    <!-- Provider (tech map infrastructures only) — picked once, sent with every row -->
    <div class="card" *ngIf="activeModule === 'tech_map_infrastructures'">
      <div class="card-header">
        <div class="card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M22 12A10 10 0 1 1 12 2M22 12h-10V2"/>
          </svg>
          Provider
        </div>
      </div>
      <p class="body-hint">
        Choose the provider these infrastructures belong to. It's taken from Kepler's
        master provider list and stored against every infrastructure in this import.
      </p>
      <div class="mitigation-form">
        <label class="mitigation-field">
          <span class="mitigation-label">Infrastructure provider</span>
          <select
            class="mitigation-input mitigation-select"
            [(ngModel)]="infraProviderId"
            [disabled]="infraProvidersLoading"
          >
            <option [ngValue]="null">
              {{ infraProvidersLoading ? 'Loading providers…' : 'No provider' }}
            </option>
            <option *ngFor="let p of infraProviders" [ngValue]="p.id">{{ p.name }}</option>
          </select>
        </label>
      </div>
    </div>

    <!-- Grouped layout (e.g. portfolio with Base Hierarchy + Initiative) -->
    <ng-container *ngIf="resolvedFieldGroups?.length; else singleCard">
      <div class="card" *ngFor="let group of resolvedFieldGroups; let first = first">
        <div class="card-header">
          <div class="card-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
            </svg>
            {{ group.label }}
          </div>
          <span class="tag tag-navy" *ngIf="first">Step 2 of 4</span>
        </div>

        <p class="body-hint" *ngIf="first">
          We've auto-suggested matches based on your column names — review and adjust as needed.
          Fields marked <strong>*</strong> are required.
        </p>

        <p class="body-hint" *ngIf="group.hint">{{ group.hint }}</p>

        <div class="note-box" *ngIf="first && moduleConfig.note">
          <strong>Note:</strong> {{ moduleConfig.note }}
        </div>

        <app-field-mapper
          [fields]="group.fields"
          [cols]="state.cols"
          [maps]="state.maps"
          (mapChange)="onMapChange($event)"
        ></app-field-mapper>
      </div>
    </ng-container>

    <!-- Single card layout (risk, okr) -->
    <ng-template #singleCard>
      <div class="card">
        <div class="card-header">
          <div class="card-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
            </svg>
            Match columns to Kepler fields
          </div>
          <span class="tag tag-navy">Step 2 of 4</span>
        </div>

        <p class="body-hint">
          We've auto-suggested matches based on your column names — review and adjust as needed.
          All fields are required.
        </p>

        <div class="note-box" *ngIf="moduleConfig.note">
          <strong>Note:</strong> {{ moduleConfig.note }}
        </div>

        <app-field-mapper
          [fields]="resolvedFields"
          [cols]="state.cols"
          [maps]="state.maps"
          (mapChange)="onMapChange($event)"
        ></app-field-mapper>
      </div>
    </ng-template>

    <div class="mapping-error" *ngIf="mappingError">
      <span class="material-symbols-outlined">error</span>
      {{ mappingError }}
    </div>

    <div class="actions">
      <button class="btn btn-outline" (click)="goStep(1)">Back</button>
      <button class="btn btn-teal" (click)="validateAndAdvance()">
        Continue
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>
    </div>
  </ng-container>

  <!-- ─── STEP 3: REVIEW ─── -->
  <ng-container *ngIf="state.step === 3">
    <div class="card">
      <div class="card-header">
        <div class="card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          Review before activating
        </div>
        <span class="tag tag-navy">Step 3 of 4</span>
      </div>
      <p class="body-hint">Check how your data will appear in Kepler before confirming.</p>
      <app-tree-preview [nodes]="treeNodes"></app-tree-preview>
    </div>

    <div class="card">
      <div class="card-title" style="margin-bottom: 12px;">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <rect x="3" y="3" width="7" height="7" rx="1"/>
          <rect x="14" y="3" width="7" height="7" rx="1"/>
          <rect x="3" y="14" width="7" height="7" rx="1"/>
          <rect x="14" y="14" width="7" height="7" rx="1"/>
        </svg>
        Summary
      </div>
      <app-summary-grid
        [labels]="moduleConfig.sumLabels"
        [colors]="moduleConfig.sumColors"
        [counts]="counts"
      ></app-summary-grid>
    </div>

    <div class="activate-row">
      <span class="activate-in-label">Activate in:</span>
      <label class="activate-checkbox">
        <input type="checkbox" [(ngModel)]="activateInKepler" /> Kepler
      </label>
      <label class="activate-checkbox">
        <input type="checkbox" [(ngModel)]="activateInRubin" /> Rubin
      </label>
    </div>

    <div class="mapping-error" *ngIf="activateError">
      <span class="material-symbols-outlined">error</span>
      {{ activateError }}
    </div>

    <div class="actions">
      <button class="btn btn-outline" (click)="goStep(2)">Back</button>
      <button class="btn btn-teal" [disabled]="activating" (click)="activate()">
        <span *ngIf="!activating" class="btn-inner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          Activate
        </span>
        <span *ngIf="activating" class="btn-inner">
          <svg class="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
            <path d="M21 12a9 9 0 11-18 0"/>
          </svg>
          Activating...
        </span>
      </button>
    </div>
  </ng-container>

  <!-- ─── STEP 4: ACTIVATED ─── -->
  <ng-container *ngIf="state.step === 4">
    <div class="card">
      <div class="success-panel">
        <div class="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="32" height="32">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
        </div>
        <div class="success-title">{{ moduleConfig.successTitle }}</div>
        <div class="success-sub">{{ successMessage }}</div>

        <div class="success-summary">
          <app-summary-grid
            [labels]="moduleConfig.sumLabels"
            [colors]="moduleConfig.sumColors"
            [counts]="counts"
          ></app-summary-grid>
        </div>

        <div class="success-actions">
          <button class="btn btn-outline" (click)="resetImport()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
              <polyline points="1 4 1 10 7 10"/>
              <path d="M3.51 15a9 9 0 1 0 .49-4.5"/>
            </svg>
            Import Again
          </button>
        </div>
      </div>
    </div>

    <!-- ─── Part 2: map mitigations onto the imported risks (risk module only) ─── -->
    <div class="card" *ngIf="activeModule === 'risk'">
      <div class="card-header">
        <div class="card-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="M9 11l3 3L22 4"/>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
          </svg>
          Map mitigations &amp; controls
        </div>
        <span class="tag tag-navy">Step 2</span>
      </div>
      <p class="body-hint">
        Risks are imported. Now enter the project name to map mitigation tasks, owners, due dates and project-level
        controls onto those risks.
      </p>

      <div class="mitigation-form">
        <label class="mitigation-field">
          <span class="mitigation-label">Project</span>
          <select
            class="mitigation-input mitigation-select"
            [(ngModel)]="mitigationProjectName"
            [disabled]="projectsLoading"
          >
            <option value="" disabled>
              {{ projectsLoading ? 'Loading projects…' : 'Select a project' }}
            </option>
            <option *ngFor="let p of projects" [value]="p.project_name">
              {{ p.project_name }}
            </option>
          </select>
        </label>
      </div>

      <div class="mapping-error" *ngIf="mitigationError">
        <span class="material-symbols-outlined">error</span>
        {{ mitigationError }}
      </div>

      <div class="mitigation-result" *ngIf="mitigationResult">
        <div class="summary-line">
          <span class="material-symbols-outlined">check_circle</span>
          Mapped {{ mitigationResult.created }} · skipped {{ mitigationResult.skipped }}
        </div>
        <div class="failed-rows-preview" *ngIf="mitigationResult.failed_rows?.rows?.length">
          <div class="failed-rows-label">Rows skipped</div>
          <app-preview-table
            [cols]="mitigationResult.failed_rows.cols"
            [rows]="mitigationResult.failed_rows.rows"
            [maxRows]="1000">
          </app-preview-table>
        </div>
        <ul class="mitigation-errors" *ngIf="!mitigationResult.failed_rows?.rows?.length && mitigationResult.errors?.length">
          <li *ngFor="let err of mitigationResult.errors">{{ err }}</li>
        </ul>
      </div>

      <div class="actions">
        <button
          class="btn btn-teal"
          [disabled]="mitigationUploading"
          (click)="uploadMitigations()"
        >
          <span *ngIf="!mitigationUploading" class="btn-inner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Map mitigations
          </span>
          <span *ngIf="mitigationUploading" class="btn-inner">
            <svg class="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
              <path d="M21 12a9 9 0 11-18 0"/>
            </svg>
            Mapping...
          </span>
        </button>
      </div>
    </div>
  </ng-container>

  </ng-container><!-- /import wizard -->

</main>
